import { Check, TriangleAlert, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import type { AccessLevel, CatalogSelectOption, TranslateFn } from "../types";
import { toTestId } from "../utils/catalog";

type ShareEmailInputProps = {
  t: TranslateFn;
  emails: string[];
  emailInput: string;
  accessLevel: AccessLevel;
  accessLevels: CatalogSelectOption<AccessLevel>[];
  blockedEmails: string[];
  submitting: boolean;
  canShare: boolean;
  canSubmitModel: boolean;
  onEmailInputChange: (value: string) => void;
  onInputBlur: () => void;
  onInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onRemoveEmail: (email: string) => void;
  onAccessLevelChange: (value: AccessLevel) => void;
  onShare: () => void;
  resolvedProjectEmails: Record<string, boolean>;
};

const ShareEmailInput = ({
  t, emails, emailInput, accessLevel, accessLevels, blockedEmails, submitting,
  canShare, canSubmitModel, onEmailInputChange, onInputBlur, onInputKeyDown,
  onRemoveEmail, onAccessLevelChange, onShare, resolvedProjectEmails,
}: ShareEmailInputProps) => (
  <div className="flex items-start gap-2 max-sm:flex-col">
    <div className="flex min-h-10 min-w-0 flex-1 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1 focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50">
      {emails.map((email) => {
        const hasError = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || blockedEmails.includes(email);
        const isAdded = resolvedProjectEmails[email];
        return (
          <span
            data-testid={`ai-catalog-share-email-chip-${toTestId(email)}`}
            key={email}
            className={`inline-flex h-7 max-w-full items-center gap-1 rounded-md px-2 text-[13px] ${isAdded ? "bg-secondary text-secondary-foreground" : hasError ? "bg-destructive text-destructive-foreground" : "bg-muted text-foreground"}`}
          >
            {hasError ? <TriangleAlert aria-hidden="true" className="size-3.5 shrink-0" /> : isAdded ? <Check aria-hidden="true" className="size-3.5 shrink-0" /> : null}
            <span className="truncate" dir="ltr">{email}</span>
            <button
              type="button"
              data-testid={`ai-catalog-share-remove-email-${toTestId(email)}`}
              aria-label={`${t("common.remove")} ${email}`}
              disabled={submitting}
              onClick={() => onRemoveEmail(email)}
              className="rounded-sm outline-none hover:bg-foreground/10 focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            >
              <X aria-hidden="true" className="size-3.5" />
            </button>
          </span>
        );
      })}
      <Input
        id="ai-catalog-share-email-input"
        autoFocus
        data-testid="ai-catalog-share-email-input-field"
        value={emailInput}
        onChange={(event) => onEmailInputChange(event.target.value)}
        onBlur={onInputBlur}
        onKeyDown={onInputKeyDown}
        placeholder={emails.length ? "" : t("aiCatalog.shareDialog.addEmails")}
        disabled={submitting}
        aria-label={t("aiCatalog.shareDialog.emails")}
        className="h-8 min-w-30 flex-1 border-0 px-1 shadow-none focus-visible:ring-0"
        dir="ltr"
      />
    </div>
    <div className="flex shrink-0 gap-2 max-sm:w-full">
      <Select value={accessLevel} onValueChange={(value) => onAccessLevelChange(value as AccessLevel)} disabled={submitting}>
        <SelectTrigger data-testid="ai-catalog-share-access-select" aria-label={t("aiCatalog.shareDialog.permission")} className="h-10 min-w-30 bg-muted max-sm:flex-1">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {accessLevels.map((option) => <SelectItem data-testid={`ai-catalog-share-access-option-${option.value}`} key={option.value} value={option.value}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button type="button" data-testid="ai-catalog-share-submit-button" onClick={onShare} disabled={!canShare || !canSubmitModel} className="h-10 min-w-22">
        {submitting ? <Spinner className="size-4" /> : t("aiCatalog.shareDialog.share")}
      </Button>
    </div>
  </div>
);

export default ShareEmailInput;
